import {
  createElement,
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
  AnimationEvent,
} from 'react';
import { useDesignTokens, renderInlineCssVars, DesignTokens } from '../../theme';
import { Button, ButtonState, ButtonVariant } from '../Button';
import { Text } from '../Text';
import { Icon } from '../Icon';
import { Close, Notification } from '@sas/react-svg';
import { useIsRTL } from '../../locale';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-MessageToast-gui-icu.properties';
import { Flow } from '../Layouts/Flow';
import {
  Collision,
  CollisionType,
  DockPosition,
  DockPositionType,
  MessageToastDisplayMode,
  MessageToastProps,
} from './types';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { BaseButton } from '../Button/__internal__';

import cx from 'classnames';
import cn from './MessageToast.css.json';
import './MessageToast.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

interface MessageToastAnimation {
  // The ability to animate is validated in the onAnimationEnd event when opening. If this event is not triggered, then unmounting is not held up by that same event when closing.
  elementAcceptsAnimation: boolean;
  prevIsOpen: boolean;
  closing: boolean;
}

type MessageToastAnimationType = keyof typeof cn.closing & keyof typeof cn.entering;

export const MessageToast = ({
  displayMode = 'primary',
  referenceElementDock = ['start', 'end'],
  toastDock = ['start', 'end'],
  offset = [0, 0],
  collision = 'fit',
  tokens,
  ...props
}: MessageToastProps) => {
  const isRTL = useIsRTL();
  const dt = useDesignTokens();
  const localize = useTextLocalizer();
  const ref = useRef<HTMLDivElement | null>(null);
  const [left, setLeft] = useState<number>();
  const [top, setTop] = useState<number>();
  const useHtmlId = createUseHtmlId('message-toast');
  const contentId = useHtmlId();

  const [
    { elementAcceptsAnimation, prevIsOpen, closing },
    setAnimation,
  ] = useState<MessageToastAnimation>({
    elementAcceptsAnimation: false,
    prevIsOpen: props.isOpen,
    closing: false,
  });

  if (props.isOpen !== prevIsOpen) {
    setAnimation((prev) => ({
      ...prev,
      prevIsOpen: props.isOpen,
      closing: !props.hideAnimation && prev.elementAcceptsAnimation && prevIsOpen,
    }));
  }

  const containerCssVars = useMemo(() => {
    return renderInlineCssVars(
      {
        ...getToastTokens(displayMode, dt),
        'spacing-m': dt.spacing.core.m,
        'spacing-xl': dt.spacing.core.xl,
        'spacing-xs': dt.spacing.core.xs,
        'spacing-none': dt.spacing.core.none,
        'accent-bd-width': dt.enableNovaTokens ? '4px' : '1px',
        'accent-bd-color': dt.enableNovaTokens ? dt.c.colorPrimary : dt.backgroundsDepth.large.bd,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'anim-dur-enter': dt.animation.duration.enter.m,
        'anim-tf-enter': dt.animation.timingFunction.enter.m,
        'anim-dur-exit': dt.animation.duration.exit.m,
        'anim-tf-exit': dt.animation.timingFunction.exit.m,
        'action-bg': dt.enableNovaTokens ? dt.backgroundsNova.page.bg : dt.backgrounds.page.bg,
        ...tokens?.(),
      },
      'toast'
    );
  }, [dt, displayMode, tokens]);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const refElement = props.referenceElementRef?.current ?? document.body;
    const [left, top] = calculateLeftTop(
      ref.current,
      toastDock,
      refElement,
      referenceElementDock,
      offset,
      collision,
      isRTL
    );

    setLeft(left);
    setTop(top);
  }, [collision, isRTL, offset, props.referenceElementRef, referenceElementDock, toastDock]);

  useEffect(updatePosition, [updatePosition]);
  const [setToastResizeRef] = useResizeObserverRef<HTMLDivElement>(() => {
    // This will be called once suspense has finished
    if (left === undefined || top === undefined) {
      // Only update position if the left/top is uninitialized
      updatePosition();
    }
  });

  const hasOffset = offset[0] !== 0 || offset[1] !== 0;

  const referenceElementDockPosition = `${referenceElementDock[0]}${referenceElementDock[1]
    .charAt(0)
    .toUpperCase()}${referenceElementDock[1].slice(1)}` as MessageToastAnimationType;

  const openingAnimClass =
    props.referenceElementRef || hasOffset
      ? cn.entering.centerCenter
      : cn.entering[referenceElementDockPosition];

  const closingAnimClass = elementAcceptsAnimation
    ? props.referenceElementRef || hasOffset
      ? cn.closing.centerCenter
      : cn.closing[referenceElementDockPosition]
    : false;

  // line-height patch specific to message toast to help keep message toast's icon and its text centered
  // NOTE: this will break if the unit is not px
  const textFontSizeInPixels =
    parseFloat(dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m) * 16;
  const textHeight = Math.round(
    textFontSizeInPixels * (dt.enableNovaTokens ? dt.fontNova.lineHeight : dt.font.lineHeight)
  );
  // the 14 is the height of the message strip's icon in pixels.
  const marginTopCalc = `${Math.round((textHeight - 14) / 2)}px`;

  return props.isOpen || closing ? (
    <div
      ref={(element) => {
        ref.current = element;
        setToastResizeRef(element);
      }}
      data-testid={props.testId}
      className={cx(cn.container, props.className)}
      style={{
        ...containerCssVars,
        visibility: left !== undefined && top !== undefined ? undefined : 'hidden',
        left,
        top,
        ...props.style,
      }}
    >
      <div
        className={cx(
          cn.containerInner,
          !props.hideAnimation && openingAnimClass,
          closing && closingAnimClass
        )}
        ref={props.toastRef}
        data-closing-anim={closingAnimClass}
        onAnimationEnd={(event: AnimationEvent) => {
          if (event.target === event.currentTarget) {
            if (closing) {
              setAnimation((prev) => ({ ...prev, prevIsOpen: false, closing: false }));
            } else {
              setAnimation({ elementAcceptsAnimation: true, prevIsOpen: false, closing: false });
            }
          }
        }}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && props.onClose) {
            props.onClose();
          }
        }}
      >
        <Icon
          className={cn.toastIcon}
          icon={Notification}
          tokens={() => {
            return { fg: 'var(--toast-fg)' };
          }}
          style={{ marginTop: marginTopCalc }}
        />
        <Flow orientation="vertical" className={cn.toastContent}>
          {props.children ? (
            <Text
              id={contentId}
              className={cn.sectionMessage}
              tokens={() => {
                return { fg: 'var(--toast-fg)' };
              }}
            >
              {props.children}
            </Text>
          ) : null}
          {props.actionButton ? (
            <BaseButton
              ref={props.actionButton?.ref}
              variant={ButtonVariant.LightSmall}
              className={cn.actionButton}
              tokens={(props) => {
                if (dt.baseReportThemeName === 'highcontrast2018' && displayMode === 'secondary') {
                  return {
                    fg: 'var(--toast-fg)',
                    filter: 'none',
                    bg: 'transparent',
                  };
                }
                if (
                  dt.themeVariant === 'high-contrast' &&
                  displayMode === 'secondary' &&
                  props.state === ButtonState.Active
                ) {
                  return {
                    filter: 'none',
                  };
                }
                return {
                  fg: 'var(--toast-fg)',
                };
              }}
              onPress={() => {
                props.actionButton?.onPress?.();
                props.onClose?.();
              }}
              text={localize(`action.button.${props.actionButton.type}.label`)}
              ariaDescribedBy={contentId}
              onBlur={props.actionButton.onBlur}
            />
          ) : null}
        </Flow>
        {props.closeable ? (
          <Button
            className={cn.closeButton}
            style={isRTL ? { left: 0, right: 'auto' } : {}}
            variant={ButtonVariant.Icon}
            onPress={props.onClose}
            tokens={(props) => {
              if (dt.enableNovaTokens) return {};
              let filter;
              if (
                dt.themeVariant === 'high-contrast' &&
                displayMode === 'secondary' &&
                props.state === ButtonState.Active
              ) {
                filter = 'none';
              }

              if (
                dt.themeVariant === 'high-contrast' &&
                displayMode === 'primary' &&
                props.state === ButtonState.Active
              ) {
                filter = 'invert(1) grayscale(1)';
              }

              if (dt.baseReportThemeName === 'highcontrast2018' && displayMode === 'secondary') {
                return {
                  fg: 'var(--toast-fg)',
                  filter: 'none',
                  bg: 'transparent',
                };
              }

              if (dt.themeVariant === 'high-contrast' && props.state === ButtonState.Active) {
                return {
                  fg: dt.stateStandardNova.active.fg,
                  filter,
                };
              }
              return { fg: 'var(--toast-fg)', filter };
            }}
            icon={{
              icon: Close,
              tooltip: localize('close.icon.tooltip.label'),
            }}
            focusable={false}
          />
        ) : null}
      </div>
    </div>
  ) : null;
};

const getToastTokens = (displayMode: MessageToastDisplayMode, dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return {
      fg: dt.c.colorTextPrimary,
      bg: dt.c.colorUiMedium,
      bd: dt.c.colorUiMedium,
      sh: dt.backgroundsDepthNova.medium.sh,
      focus:
        dt.themeVariant === 'high-contrast'
          ? 'var(--focus-light-bg-color)'
          : 'var(--focus-dark-bg-color)',
    };
  } else {
    switch (dt.baseReportThemeName) {
      case 'highcontrast2018':
      case 'highcontrast2017':
        return displayMode === 'primary'
          ? {
              fg: dt.c.white,
              bg: dt.c.black,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-light-bg-color)',
            }
          : {
              fg: dt.c.black,
              bg: dt.c.secondary,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: dt.c.black,
            };
    }
    switch (dt.themeVariant) {
      case 'dark':
        return displayMode === 'primary'
          ? {
              fg: dt.c.white,
              bg: dt.c.p_2,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-dark-bg-color)',
            }
          : {
              fg: dt.c.white,
              bg: dt.c.secondary,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: dt.c.colorTextDefault,
            };
      case 'high-contrast':
        return displayMode === 'primary'
          ? {
              fg: dt.c.white,
              bg: dt.c.black,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-light-bg-color)',
            }
          : {
              fg: dt.c.black,
              bg: dt.c.e2,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-dark-bg-color)',
            };

      default:
        return displayMode === 'primary'
          ? {
              fg: dt.c.black,
              bg: dt.c.white,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-light-bg-color)',
            }
          : {
              fg: dt.c.white,
              bg: dt.c.secondary,
              bd: dt.backgroundsDepth.large.bd,
              sh: dt.backgroundsDepth.large.sh,
              focus: 'var(--focus-dark-bg-color)',
            };
    }
  }
};

const getFlippedPosition = (dockPositionType: DockPositionType): DockPositionType => {
  switch (dockPositionType) {
    case 'start':
      return 'end';
    case 'center':
      return 'center';
    case 'end':
      return 'start';
  }
};

const getDockPoint = (params: {
  left: number;
  top: number;
  width: number;
  height: number;
  dockPosition: DockPosition;
  isRTL: boolean;
}): { left: number; top: number; flippedLeft: number; flippedTop: number } => {
  const {
    width,
    height,
    left,
    top,
    dockPosition: [dockPositionX, dockPositionY],
    isRTL,
  } = params;

  const getLeft = (dockPositionX: DockPositionType): number => {
    switch (dockPositionX) {
      case 'start':
        return isRTL ? left + width : left;
      case 'center':
        return left + width / 2;
      case 'end':
        return isRTL ? left : left + width;
    }
  };

  const getTop = (dockPositionY: DockPositionType): number => {
    switch (dockPositionY) {
      case 'start':
        return top;
      case 'center':
        return top + height / 2;
      case 'end':
        return top + height;
    }
  };

  return {
    left: getLeft(dockPositionX),
    top: getTop(dockPositionY),
    flippedLeft: getLeft(getFlippedPosition(dockPositionX)),
    flippedTop: getTop(getFlippedPosition(dockPositionY)),
  };
};

const getValueByCollision = (
  value: number,
  flippedValue: number,
  minimum: number,
  maximum: number,
  collisionType: CollisionType
): number => {
  const getHiddenLength = (value: number) => {
    if (value >= minimum && value <= maximum) return 0;
    return value > maximum ? value - maximum : minimum - value;
  };
  switch (collisionType) {
    case 'none':
      return value;
    case 'fit':
      return Math.min(maximum, Math.max(minimum, value));
    case 'flip':
      const hiddenLength = getHiddenLength(value);
      const flippedHiddenLength = getHiddenLength(flippedValue);
      return hiddenLength <= flippedHiddenLength ? value : flippedValue;
  }
};

const calculateLeftTop = (
  toastElement: HTMLElement,
  toastDock: DockPosition,
  refElement: HTMLElement,
  refElementDock: DockPosition,
  offset: [number, number],
  collision: Collision,
  isRTL: boolean
): [number | undefined, number | undefined] => {
  const { width: toastWidth, height: toastHeight } = toastElement.getBoundingClientRect();
  if (toastWidth === 0 || toastHeight === 0) return [undefined, undefined];
  const refElementRect = refElement.getBoundingClientRect();
  const {
    left: refElementLeft,
    top: refElementTop,
    flippedLeft: flippedRefElementLeft,
    flippedTop: flippedRefElementTop,
  } = getDockPoint({
    left: refElementRect.left, // Use left/top rather than x/y because Legacy Edge does not have x/y
    top: refElementRect.top,
    width: refElementRect.width,
    height: refElementRect.height,
    dockPosition: refElementDock,
    isRTL,
  });

  const {
    left: toastLeft,
    top: toastTop,
    flippedLeft: flippedToastLeft,
    flippedTop: flippedToastTop,
  } = getDockPoint({
    left: 0,
    top: 0,
    width: toastWidth,
    height: toastHeight,
    dockPosition: toastDock,
    isRTL,
  });

  const left = refElementLeft - toastLeft + offset[0];
  const top = refElementTop - toastTop + offset[1];
  const flippedLeft = flippedRefElementLeft - flippedToastLeft + offset[0];
  const flippedTop = flippedRefElementTop - flippedToastTop + offset[1];

  const bodyRect = document.body.getBoundingClientRect();
  const maxTop = bodyRect.top + bodyRect.height - toastHeight;
  const minTop = bodyRect.top;
  const maxLeft = bodyRect.left + bodyRect.width - toastWidth;
  const minLeft = bodyRect.left;

  const collisionX = typeof collision === 'string' ? collision : collision[0];
  const collisionY = typeof collision === 'string' ? collision : collision[1];

  const resultLeft = getValueByCollision(left, flippedLeft, minLeft, maxLeft, collisionX);
  const resultTop = getValueByCollision(top, flippedTop, minTop, maxTop, collisionY);

  return [resultLeft, resultTop];
};
